<template>
  <div class="image-upload-container">
    <el-upload
      :action="uploadUrl"
      :on-success="handleUploadSuccess"
      :on-error="handleUploadError"
      :on-progress="handleUploadProgress"
      :before-upload="handleBeforeUpload"
      :on-remove="handleRemove"
      :on-preview="handlePictureCardPreview"
      :accept="accept"
      :file-list="fileList"
      :limit="maxCount"
      :on-exceed="handleExceed"
      :list-type="listType"
      class="image-uploader"
    >
      <i class="el-icon-plus"></i>
    </el-upload>

    <!-- 图片预览对话框 -->
    <el-dialog
      :visible.sync="previewVisible"
      width="50%"
      center
      :append-to-body="true"
    >
      <img :src="previewImageUrl" style="width: 100%; height: auto" />
    </el-dialog>
  </div>
</template>

<script>
import { uploadUrl, baseUrl } from "@/axios/baseUrl";

export default {
  name: "ImageUpload",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    accept: {
      type: String,
      default: ".jpg,.jpeg,.png,.gif",
    },
    maxSize: {
      type: Number,
      default: 10 * 1024 * 1024, // 10MB
    },
    maxCount: {
      type: Number,
      default: 20,
    },
    listType: {
      type: String,
      default: "picture-card",
    },
  },
  data() {
    return {
      uploadUrl: uploadUrl,
      previewVisible: false,
      previewImageUrl: "",
      fileList: [],
    };
  },
  computed: {
    // 获取完整的图片URL
    getFullImageUrl() {
      return (url) => {
        if (!url) return "";
        // 如果已经是完整URL，直接返回
        if (url.startsWith("http://") || url.startsWith("https://")) {
          return url;
        }
        // 如果是相对路径，添加baseUrl
        return baseUrl + url;
      };
    },
  },
  watch: {
    value: {
      handler(newVal) {
        // 将value数组转换为fileList格式
        // 过滤掉blob URL，只保留有效的服务器URL
        const validUrls = newVal.filter(
          (url) => url && !url.startsWith("blob:")
        );
        this.fileList = validUrls.map((url, index) => ({
          name: `图片${index + 1}`,
          url: this.getFullImageUrl(url), // 使用完整的URL用于显示
          originalUrl: url, // 保存原始URL用于提交
          uid: Date.now() + index,
        }));
      },
      immediate: true,
    },
  },
  methods: {
    // 文件上传前验证
    handleBeforeUpload(file) {
      const allowedTypes = ["jpg", "jpeg", "png", "gif"];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (!allowedTypes.includes(fileExtension)) {
        this.$message.error("只支持上传 JPG、PNG、GIF 格式的图片");
        return false;
      }

      // 文件大小限制
      if (file.size > this.maxSize) {
        this.$message.error(`图片大小不能超过 ${this.maxSize / 1024 / 1024}MB`);
        return false;
      }

      return true;
    },
    // 文件上传进度处理
    handleUploadProgress(event, file, fileList) {
      // 向上传递上传中状态
      this.$emit("uploading", true);
    },
    // 文件上传成功处理
    handleUploadSuccess(res, file, fileList) {
      // 上传完成，向上传递上传完成状态
      this.$emit("uploading", false);

      if (res.code === 200) {
        // 更新fileList，将后端返回的文件路径设置到file对象中
        const updatedFileList = fileList.map((f) => {
          if (f.uid === file.uid) {
            return {
              ...f,
              url: this.getFullImageUrl(res.data), // 使用完整的URL用于显示
              originalUrl: res.data, // 保存原始URL用于提交
              response: res,
            };
          }
          return f;
        });
        this.fileList = updatedFileList;

        // 提取URL数组并触发v-model更新（使用原始URL）
        const urlList = updatedFileList
          .map((file) => file.originalUrl || file.response?.data)
          .filter((url) => url && !url.startsWith("blob:"));
        this.$emit("input", urlList);
        this.$emit("change", urlList);

        this.$message.success("图片上传成功");
      } else {
        this.$message.error(res.message || "图片上传失败");
      }
    },
    // 文件上传失败处理
    handleUploadError() {
      // 上传失败，向上传递上传完成状态
      this.$emit("uploading", false);
      this.$message.error("图片上传失败");
    },
    // 删除图片
    handleRemove(file, fileList) {
      // 更新fileList
      this.fileList = fileList;

      // 提取URL数组并触发v-model更新（使用原始URL）
      const urlList = fileList
        .map((file) => file.originalUrl || file.url || file.response?.data)
        .filter((url) => url && !url.startsWith("blob:"));
      this.$emit("input", urlList);
      this.$emit("change", urlList);

      // this.$message.success('图片删除成功')
    },
    // 预览图片
    handlePictureCardPreview(file) {
      this.previewImageUrl = file.url || file.response?.data;
      this.previewVisible = true;
    },
    // 超出限制处理
    handleExceed() {
      this.$message.warning(`最多只能上传 ${this.maxCount} 张图片`);
    },
  },
};
</script>

<style scoped lang="less">
.image-upload-container {
  .image-uploader {
    .el-upload--picture-card {
      width: 40px;
      height: 40px;
      line-height: 40px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      background-color: #f5f7fa;

      &:hover {
        border-color: #409eff;
        color: #409eff;
      }
    }

    /deep/ .el-upload-list--picture-card {
      .el-upload-list__item {
        width: 40px !important;
        height: 40px !important;
        border: 1px solid #dcdfe6;
        border-radius: 4px;

        .el-upload-list__item-thumbnail {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .el-upload-list__item-actions {
          background: rgba(0, 0, 0, 0.5);

          .el-upload-list__item-preview,
          .el-upload-list__item-delete {
            color: white;

            &:hover {
              color: #409eff;
            }
          }
        }
      }
    }
    /deep/ .el-upload--picture-card {
      width: 40px;
      height: 40px;
      line-height: 40px;
    }
  }
}
</style>
